import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import OverrideRevertBadge from '../../../shared/OverrideRevertBadge';
import { Pill } from '../../../shared/Pill';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// Roles (issue #252, vis_user_management.md Concept 5) — Admin only. Works
// like WordPress/WooCommerce's role editor: pick a Role, then tick Create,
// Read, Update and Delete against each module, which applies to every
// screen in it. A screen can differ from its module; a screen that does is
// flagged as an override, and its ↺ badge sets it back to following the
// module. A screen with its own permission (the Virtual Packer) never
// follows its module: it's off until ticked on the screen itself. Admin
// always has every permission, so it has no grid.

const ACTIONS = [
  { key: 'create', label: 'Create' },
  { key: 'read', label: 'Read' },
  { key: 'update', label: 'Update' },
  { key: 'delete', label: 'Delete' },
];

// Grid order, top to bottom.
const MODULE_ORDER = [
  { key: 'home', label: 'Home' },
  { key: 'crm', label: 'CRM' },
  { key: 'sales', label: 'Sales' },
  { key: 'purchasing', label: 'Purchasing' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'receivables', label: 'Receivables' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'market-intel', label: 'Market Intel' },
  { key: 'global', label: 'Platform' },
];

const NONE = { create: false, read: false, update: false, delete: false };

function samePerm(a, b) {
  return ACTIONS.every(({ key }) => !!a[key] === !!b[key]);
}

// Create, Update and Delete all need Read to reach the screen at all, so
// ticking one of them ticks Read too, and unticking Read clears them.
function toggled(perm, action, checked) {
  const next = { ...perm, [action]: checked };
  if (action === 'read' && !checked) return { ...NONE };
  if (action !== 'read' && checked) next.read = true;
  return next;
}

function screensByModule(moduleNavigation, adminOnlyScreens) {
  const result = {};
  for (const { key } of MODULE_ORDER) {
    const seen = new Set();
    result[key] = (moduleNavigation[key] || [])
      .flatMap((section) => section.items)
      .filter((item) => !item.pageKey.includes('?') && !adminOnlyScreens.has(item.pageKey))
      .filter((item) => (seen.has(item.pageKey) ? false : seen.add(item.pageKey)));
  }
  return result;
}

const cellStyle = { padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #eef0f3' };

function PermissionGrid({ modules, screens, onChange, screenGroups, ownScreens }) {
  const [expanded, setExpanded] = useState(() => new Set(
    MODULE_ORDER.filter(({ key }) => (screenGroups[key] || []).some((item) => screens[item.pageKey] && !ownScreens.has(item.pageKey))).map(({ key }) => key)
  ));
  // What a screen has when nothing is ticked against it.
  const baseFor = (moduleKey, pageKey) => (ownScreens.has(pageKey) ? NONE : modules[moduleKey] || NONE);

  const toggleExpanded = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const setModule = (moduleKey, action, checked) => {
    const nextModules = { ...modules, [moduleKey]: toggled(modules[moduleKey] || NONE, action, checked) };
    // An override that now matches its module goes back to following it.
    const nextScreens = { ...screens };
    for (const item of screenGroups[moduleKey] || []) {
      if (ownScreens.has(item.pageKey)) continue;
      if (nextScreens[item.pageKey] && samePerm(nextScreens[item.pageKey], nextModules[moduleKey])) delete nextScreens[item.pageKey];
    }
    onChange(nextModules, nextScreens);
  };

  const setScreen = (moduleKey, pageKey, action, checked) => {
    const base = baseFor(moduleKey, pageKey);
    const next = toggled(screens[pageKey] || base, action, checked);
    const nextScreens = { ...screens };
    if (samePerm(next, base)) delete nextScreens[pageKey];
    else nextScreens[pageKey] = next;
    onChange(modules, nextScreens);
  };

  const revertScreen = (pageKey) => {
    const nextScreens = { ...screens };
    delete nextScreens[pageKey];
    onChange(modules, nextScreens);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '44%' }} />
          {ACTIONS.map(({ key }) => <col key={key} style={{ width: '14%' }} />)}
        </colgroup>
        <thead className="list-head" style={{ fontSize: 13 }}>
          <tr>
            <th style={{ padding: '10px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Module / Screen</th>
            {ACTIONS.map(({ key, label }) => (
              <th key={key} style={{ padding: '10px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MODULE_ORDER.map(({ key: moduleKey, label: moduleLabel }) => {
            const modulePerm = modules[moduleKey] || NONE;
            const items = screenGroups[moduleKey] || [];
            const overrideCount = items.filter((item) => screens[item.pageKey] && !ownScreens.has(item.pageKey)).length;
            const isOpen = expanded.has(moduleKey);
            return (
              <React.Fragment key={moduleKey}>
                <tr style={{ background: '#f3f5f8' }}>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(moduleKey)}
                      title={isOpen ? 'Hide screens' : 'Show screens'}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: '#1a1a1a' }}
                    >
                      {isOpen ? '▾' : '▸'} {moduleLabel}
                    </button>
                    <span style={{ fontWeight: 400, color: '#5a6b85', fontSize: 12, marginLeft: 8 }}>
                      {items.length} screens{overrideCount ? ` · ${overrideCount} override${overrideCount === 1 ? '' : 's'}` : ''}
                    </span>
                  </td>
                  {ACTIONS.map(({ key: action }) => (
                    <td key={action} style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={!!modulePerm[action]}
                        onChange={(e) => setModule(moduleKey, action, e.target.checked)}
                        aria-label={`${moduleLabel} ${action}`}
                      />
                    </td>
                  ))}
                </tr>
                {isOpen && items.map((item) => {
                  const own = ownScreens.has(item.pageKey);
                  const override = own ? null : screens[item.pageKey];
                  const perm = screens[item.pageKey] || baseFor(moduleKey, item.pageKey);
                  return (
                    <tr key={item.pageKey} style={override ? { background: '#fff8e6' } : undefined}>
                      <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: 30 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {item.label}
                          {own && <Pill color="#555" background="#eceef1" title={`Off unless ticked here, whatever ${moduleLabel} has`}>Own permission</Pill>}
                          {override && (
                            <>
                              <Pill color="#8a5a00" background="#fff3d6" title={`Differs from ${moduleLabel}`}>Override</Pill>
                              <OverrideRevertBadge onClick={() => revertScreen(item.pageKey)} title={`Follow ${moduleLabel} again`} />
                            </>
                          )}
                        </span>
                      </td>
                      {ACTIONS.map(({ key: action }) => (
                        <td key={action} style={cellStyle}>
                          <input
                            type="checkbox"
                            checked={!!perm[action]}
                            onChange={(e) => setScreen(moduleKey, item.pageKey, action, e.target.checked)}
                            aria-label={`${item.label} ${action}`}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RoleEditor({ role, screenGroups, ownScreens, onSaved, onCancel }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [name, setName] = useState(role.name);
  const [modules, setModules] = useState(role.modules || {});
  const [screens, setScreens] = useState(role.screens || {});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`/api/roles/${role.id}`, { name, modules, screens });
      dismissError();
      onSaved(res.data.data);
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14, maxWidth: 360 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Role Name</label>
        <input className="styled-input" value={name} onChange={(e) => setName(e.target.value)} required style={{ width: '100%', padding: '6px 10px', fontSize: 14 }} />
      </div>
      <PermissionGrid
        modules={modules}
        screens={screens}
        screenGroups={screenGroups}
        ownScreens={ownScreens}
        onChange={(nextModules, nextScreens) => { setModules(nextModules); setScreens(nextScreens); }}
      />
      <div style={{ marginTop: 12 }}>
        <SaveButton loading={saving} />
        <CancelButton onClick={onCancel} disabled={saving} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

function RoleTable({ moduleNavigation, adminOnlyScreens, ownPermissionScreens = new Set() }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const screenGroups = useMemo(() => screensByModule(moduleNavigation, adminOnlyScreens), [moduleNavigation, adminOnlyScreens]);
  const busy = adding || editingId !== null;

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/roles');
      setRoles(res.data.data || []);
    } catch (err) {
      showError(err, 'Could not load roles');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddSaving(true);
    try {
      const res = await axios.post('/api/roles', { name: newName });
      setAdding(false);
      setNewName('');
      dismissError();
      await fetchRoles();
      setEditingId(res.data.data.id);
    } catch (err) {
      showError(err, 'Add failed');
    }
    setAddSaving(false);
  };

  const handleDelete = async (role) => {
    if (!(await askConfirm(`Delete the ${role.name} role?`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/roles/${role.id}`);
      dismissError();
      fetchRoles();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const editingRole = roles.find((role) => role.id === editingId);

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Roles</h2>
        <button
          onClick={() => setAdding(true)}
          disabled={busy}
          title={busy ? 'Finish editing first' : undefined}
          style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
        >
          Add Role
        </button>
      </div>

      {adding && (
        <form onSubmit={handleAdd} style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Role Name</label>
          <input className="styled-input" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} required style={{ width: 360, maxWidth: '100%', padding: '6px 10px', fontSize: 14 }} />
          <div style={{ marginTop: 12 }}>
            <SaveButton loading={addSaving} />
            <CancelButton onClick={() => { setAdding(false); setNewName(''); }} disabled={addSaving} style={{ marginLeft: 8 }} />
          </div>
        </form>
      )}

      {editingRole && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <RoleEditor
            key={editingRole.id}
            role={editingRole}
            screenGroups={screenGroups}
            ownScreens={ownPermissionScreens}
            onSaved={() => { setEditingId(null); fetchRoles(); }}
            onCancel={() => setEditingId(null)}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '60%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Role</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Held By</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && !roles.length ? (
              <tr><td colSpan={3} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : roles.map((role) => (
              <tr
                key={role.id}
                className="list-row"
                onClick={() => { if (!busy && !role.is_admin) setEditingId(role.id); }}
                style={{ cursor: busy || role.is_admin ? 'default' : 'pointer' }}
                title={busy || role.is_admin ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {role.name}
                    {role.is_admin && <Pill title="Always has every permission, plus user and role management">Every permission</Pill>}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, color: role.holders.length ? undefined : '#888' }}>
                  {role.holders.length ? role.holders.map((h) => h.name).join(', ') : 'Nobody'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  {!role.is_admin && (
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: busy, disabledTitle: busy ? 'Finish editing first' : undefined, onClick: () => setEditingId(role.id) },
                        { key: 'delete', label: 'Delete', danger: true, disabled: busy, disabledTitle: busy ? 'Finish editing first' : undefined, onClick: () => handleDelete(role) },
                      ]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RoleTable;
